import { useCallback, useMemo } from 'react';

import { ApiMethod } from '../models/api-types.type';
import {
    NetworkBrandStatus,
    NetworkColour,
    NetworkFormat,
    NetworkLinkEvidence,
    NetworkLinkMethod,
    NetworkLinkStatus,
    VendorNetworkSettings,
} from '../models/network.model';
import { useAuthContext } from './use-auth-context.hook';

/**
 * The retailer's side of the Ardealy Network — pms-api-v2
 * `src/pms/pms-network/retailer` (`services/pms/vendors/me/network/*`).
 * Response shapes are the API's own; the shared nouns are in network.model.ts.
 */

export interface NetworkScanState {
    id: string | null;
    status: 'running' | 'done' | 'failed';
    startedAt: string;
    finishedAt: string | null;
    result: { suggested: number; confirmed: number; skipped: number; byMethod?: Record<string, number> } | null;
    error: string | null;
}

export interface NetworkAllowance {
    /** -1 = unlimited. */
    limit: number;
    used: number;
    /** null = unlimited. */
    remaining: number | null;
}

export interface RetailerNetworkSummary {
    linked: { variants: number; products: number; networkProducts: number };
    suggested: number;
    brandsCarried: number;
    available: number;
    lastScan: NetworkScanState | null;
    /** False until the matching engine is wired on the server: the scan answers 503. */
    matcherAvailable: boolean;
    allowance: NetworkAllowance;
    settings: VendorNetworkSettings & { shareDemand: boolean; listed: boolean };
}

/**
 * Snap & Find for this shop (pms-api-v2 `pms-network/snap`,
 * `services/pms/vendors/me/snap-find`): a shopper's photo → this shop's closest products.
 * `available` is false while SurfaceID is down or nothing could be fingerprinted;
 * `indexing` is there only while the catalogue is still being fingerprinted.
 */
export interface SnapFindSettings {
    enabled: boolean;
    available: boolean;
    model?: string;
    indexing?: { embedded: number; total: number };
    last30Days: { searches: number; matched: number; chosen: number };
}

export interface NetworkBrandRef {
    slug: string;
    name: string;
    logoUrl?: string;
}

export interface NetworkProductCard {
    uuid: string;
    name: string;
    ref?: string;
    eans: string[];
    format?: NetworkFormat;
    colour?: NetworkColour;
    finish?: string;
    material?: string;
    appearance?: string;
    pattern?: string;
    visualizationType?: 'surface' | 'model3d' | 'image_only';
    image?: string;
    textureUrl?: string;
    brand: NetworkBrandRef | null;
    collection: { slug: string; name: string } | null;
}

export interface NetworkMatch {
    id: string;
    status: NetworkLinkStatus;
    method: NetworkLinkMethod;
    score: number;
    evidence: NetworkLinkEvidence;
    decidedBy?: string;
    decidedAt?: string;
    createdAt?: string;
    alternatives: number;
    product: {
        uuid: string;
        id: string;
        name: string;
        sku?: string;
        brand?: string;
        ean?: string;
        image?: string;
        format?: { width: number; height: number; unit: string };
        colour?: { name: string; hex?: string };
    } | null;
    networkProduct: NetworkProductCard | null;
}

export interface Paged<T> {
    items: T[];
    total: number;
    page: number;
    limit: number;
    nextPage: number | null;
}

export interface DecisionOutcome {
    id: string;
    ok: boolean;
    status?: 'confirmed' | 'rejected';
    reason?: 'not_found' | 'product_gone' | 'another_match_confirmed';
    superseded?: string[];
}

export interface NetworkBrandListItem {
    id: string;
    slug: string;
    name: string;
    country?: string;
    logoUrl?: string;
    website?: string;
    description?: string;
    materials: string[];
    status: NetworkBrandStatus;
    products: number;
    collections: number;
    inMyCatalog: number;
    carried: boolean;
}

export interface FacetValue {
    value: string;
    count: number;
}

export interface NetworkBrandDetail {
    brand: Omit<NetworkBrandListItem, 'products' | 'collections' | 'inMyCatalog'>;
    totals: { products: number; inMyCatalog: number };
    collections: Array<{
        slug: string;
        name: string;
        material?: string;
        appearance?: string;
        description?: string;
        coverImageUrl?: string;
        products: number;
        inMyCatalog: number;
    }>;
    facets: {
        materials: FacetValue[];
        appearances: FacetValue[];
        colorFamilies: FacetValue[];
        formats: Array<{ key: string; width: number; height: number; unit: string; count: number }>;
    };
}

export interface BrandProductFilters {
    collection?: string;
    material?: string;
    appearance?: string;
    colorFamily?: string;
    format?: string;
    q?: string;
    inCatalog?: 'yes' | 'no';
    page?: number;
    limit?: number;
}

export type BrandProduct = NetworkProductCard & { inMyCatalog: boolean; myProduct: { uuid: string; id?: string } | null };

export type ImportSkipReason =
    | 'not_found'
    | 'not_published'
    | 'discontinued'
    | 'already_in_catalog'
    | 'not_visualizer_ready'
    | 'family_not_found'
    | 'family_not_enabled'
    | 'plan_limit';

export interface NetworkImportResult {
    created: number;
    createdProducts: number;
    skipped: number;
    failed: number;
    skippedByReason: Partial<Record<ImportSkipReason, number>>;
    allowance: NetworkAllowance;
    items: Array<{
        networkProductUuid: string;
        name?: string;
        status: 'created' | 'skipped' | 'failed';
        reason?: ImportSkipReason;
        message?: string;
        productUuid?: string;
        productId?: string;
    }>;
}

const BASE = '/services/pms/vendors/me/network';
const SNAP_FIND = '/services/pms/vendors/me/snap-find';

const qs = (params: Record<string, unknown>) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
    const s = query.toString();
    return s ? `?${s}` : '';
};

export const useNetworkApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const summary = useCallback(
        async (): Promise<RetailerNetworkSummary> => (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/summary`)) as RetailerNetworkSummary,
        [sendAuthGuardedRequest],
    );

    const matches = useCallback(
        async (status: NetworkLinkStatus, page = 1, limit = 20): Promise<Paged<NetworkMatch>> =>
            (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/matches${qs({ status, page, limit })}`)) as Paged<NetworkMatch>,
        [sendAuthGuardedRequest],
    );

    const decide = useCallback(
        async (id: string, status: 'confirmed' | 'rejected'): Promise<DecisionOutcome & { match?: NetworkMatch }> =>
            (await sendAuthGuardedRequest(ApiMethod.PATCH, `${BASE}/matches/${encodeURIComponent(id)}`, { status })) as DecisionOutcome & {
                match?: NetworkMatch;
            },
        [sendAuthGuardedRequest],
    );

    const decideMany = useCallback(
        async (ids: string[], status: 'confirmed' | 'rejected'): Promise<{ results: DecisionOutcome[]; decided: number }> =>
            (await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/matches/bulk`, { ids, status })) as { results: DecisionOutcome[]; decided: number },
        [sendAuthGuardedRequest],
    );

    const scan = useCallback(
        async (): Promise<{ scan: NetworkScanState }> => (await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/scan`, {})) as { scan: NetworkScanState },
        [sendAuthGuardedRequest],
    );

    const brands = useCallback(
        async (): Promise<{ items: NetworkBrandListItem[] }> =>
            (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/brands`)) as { items: NetworkBrandListItem[] },
        [sendAuthGuardedRequest],
    );

    const brand = useCallback(
        async (slug: string): Promise<NetworkBrandDetail> =>
            (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/brands/${encodeURIComponent(slug)}`)) as NetworkBrandDetail,
        [sendAuthGuardedRequest],
    );

    const brandProducts = useCallback(
        async (slug: string, filters: BrandProductFilters): Promise<Paged<BrandProduct>> =>
            (await sendAuthGuardedRequest(
                ApiMethod.GET,
                `${BASE}/brands/${encodeURIComponent(slug)}/products${qs({ ...filters })}`,
            )) as Paged<BrandProduct>,
        [sendAuthGuardedRequest],
    );

    /** Every uuid matching the filters that is not in the catalogue yet — "add the whole collection". */
    const brandProductUuids = useCallback(
        async (slug: string, filters: BrandProductFilters): Promise<{ uuids: string[]; total: number }> =>
            (await sendAuthGuardedRequest(
                ApiMethod.GET,
                `${BASE}/brands/${encodeURIComponent(slug)}/products${qs({ ...filters, page: undefined, limit: undefined, idsOnly: 1 })}`,
            )) as { uuids: string[]; total: number },
        [sendAuthGuardedRequest],
    );

    const importProducts = useCallback(
        async (networkProductUuids: string[]): Promise<NetworkImportResult> =>
            (await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/import`, { networkProductUuids })) as NetworkImportResult,
        [sendAuthGuardedRequest],
    );

    const settings = useCallback(
        async (): Promise<RetailerNetworkSummary['settings']> =>
            (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/settings`)) as RetailerNetworkSummary['settings'],
        [sendAuthGuardedRequest],
    );

    const updateSettings = useCallback(
        async (patch: { shareDemand?: boolean; listed?: boolean }): Promise<RetailerNetworkSummary['settings']> =>
            (await sendAuthGuardedRequest(ApiMethod.PATCH, `${BASE}/settings`, patch)) as RetailerNetworkSummary['settings'],
        [sendAuthGuardedRequest],
    );

    const snapFind = useCallback(
        async (): Promise<SnapFindSettings> => (await sendAuthGuardedRequest(ApiMethod.GET, SNAP_FIND)) as SnapFindSettings,
        [sendAuthGuardedRequest],
    );

    /** Switching it on also starts fingerprinting the catalogue. */
    const updateSnapFind = useCallback(
        async (enabled: boolean): Promise<SnapFindSettings> =>
            (await sendAuthGuardedRequest(ApiMethod.PATCH, SNAP_FIND, { enabled })) as SnapFindSettings,
        [sendAuthGuardedRequest],
    );

    // Memoised: pages put this object in effect dependencies.
    return useMemo(
        () => ({ summary, matches, decide, decideMany, scan, brands, brand, brandProducts, brandProductUuids, importProducts, settings, updateSettings, snapFind, updateSnapFind }),
        [summary, matches, decide, decideMany, scan, brands, brand, brandProducts, brandProductUuids, importProducts, settings, updateSettings, snapFind, updateSnapFind],
    );
};
